/**
 * Groups feature — queries + mutations.
 *
 * Backed by internal/tenant/group.AdminHandler, mounted at
 * /api/v1/tenants/{tenant_id}/groups via the
 * WithPathTenant wrapper.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  groupMembersResponseSchema,
  groupSchema,
  groupsListResponseSchema,
  type CreateGroupInput,
  type Group,
  type GroupMember,
  type GroupsListResponse,
  type GroupsListSearch,
  type UpdateGroupInput,
  membershipRulePreviewSchema,
  membershipRuleSchema,
  membershipRulesSchema,
  type MembershipRules,
  type MembershipRule,
  type MembershipRulePreview,
  groupNestingsSchema,
  nestPreviewSchema,
  type GroupNestings,
  type NestPreview,
  groupOwnersResponseSchema,
  type GroupOwner,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/groups`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchGroups(
  tenantID: string,
  search: GroupsListSearch,
): Promise<GroupsListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: {
      q: search.q,
      sort_by: search.sortBy,
      sort_order: search.sortOrder,
      offset: search.offset,
    },
  });
  return groupsListResponseSchema.parse(raw);
}

export async function fetchGroup(
  tenantID: string,
  groupId: string,
): Promise<Group> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(groupId)}`,
  );
  return groupSchema.parse(raw);
}

export async function fetchGroupMembers(
  tenantID: string,
  groupId: string,
): Promise<readonly GroupMember[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(groupId)}/members`,
  );
  return groupMembersResponseSchema.parse(raw).data;
}

// ── Queries ───────────────────────────────────────────────────────────────

export function useGroupsQuery(tenantID: string, search: GroupsListSearch) {
  return useQuery<GroupsListResponse, ApiError>({
    queryKey: queryKeys.groups.list(tenantID, search),
    queryFn: () => fetchGroups(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useGroupQuery(tenantID: string, groupId: string) {
  return useQuery<Group, ApiError>({
    queryKey: queryKeys.groups.detail(tenantID, groupId),
    queryFn: () => fetchGroup(tenantID, groupId),
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

export function useGroupMembersQuery(tenantID: string, groupId: string) {
  return useQuery<readonly GroupMember[], ApiError>({
    queryKey: queryKeys.groups.members(tenantID, groupId),
    queryFn: () => fetchGroupMembers(tenantID, groupId),
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateGroupMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Group, ApiError, CreateGroupInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => groupSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.all(tenantID) });
    },
  });
}

export function useUpdateGroupMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<Group, ApiError, UpdateGroupInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(groupId)}`, {
          method: "PATCH",
          body: input,
        }).then((raw) => groupSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.groups.detail(tenantID, groupId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

export function useDeleteGroupMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (groupId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(groupId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, groupId) => {
      qc.removeQueries({ queryKey: queryKeys.groups.detail(tenantID, groupId) });
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

/**
 * useAddGroupMembersMutation adds one or more users to a group in a
 * single request (POST /members with { user_ids }). The backend adds
 * them atomically and idempotently, so the caller doesn't fan out N
 * calls — one round trip, one step-up ceremony, one audit batch. The
 * mutation input is the (de-duplicated) set of user ids to add.
 */
export function useAddGroupMembersMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, readonly string[]>({
    mutationFn: (userIds) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(groupId)}/members`,
          {
            method: "POST",
            body: { user_ids: userIds },
          },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.groups.members(tenantID, groupId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.groups.detail(tenantID, groupId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

export function useRemoveGroupMemberMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (userId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.groups.members(tenantID, groupId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.groups.detail(tenantID, groupId),
      });
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

// ── Membership rules (P4 slice 0) ─────────────────────────────────────────
//
// One rule per group. The GET answers 404 when no rule exists, which the
// query maps to null: "no rule" is the ordinary state of most groups, not
// an error the page should render red.

function ruleBase(tenantID: string, groupId: string): string {
  return `${base(tenantID)}/${encodeURIComponent(groupId)}/membership-rule`;
}

export function useGroupRuleQuery(tenantID: string, groupId: string) {
  return useQuery<MembershipRule | null, ApiError>({
    queryKey: queryKeys.groups.rule(tenantID, groupId),
    queryFn: async () => {
      try {
        const raw = await apiFetch<unknown>(ruleBase(tenantID, groupId));
        return membershipRuleSchema.parse(raw);
      } catch (err) {
        if (err instanceof Error && "status" in err && (err as { status?: number }).status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

function invalidateRule(qc: ReturnType<typeof useQueryClient>, tenantID: string, groupId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.groups.rules(tenantID) });
  qc.invalidateQueries({ queryKey: queryKeys.groups.rule(tenantID, groupId) });
  qc.invalidateQueries({ queryKey: queryKeys.groups.detail(tenantID, groupId) });
  qc.invalidateQueries({ queryKey: queryKeys.groups.members(tenantID, groupId) });
}

export function useSaveGroupRuleMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<MembershipRule, ApiError, string>({
    mutationFn: (expression) =>
      withStepUp(() =>
        apiFetch<unknown>(ruleBase(tenantID, groupId), {
          method: "PUT",
          body: { expression },
        }).then((raw) => membershipRuleSchema.parse(raw)),
      ),
    onSuccess: () => invalidateRule(qc, tenantID, groupId),
  });
}

export function usePreviewGroupRuleMutation(tenantID: string, groupId: string) {
  return useMutation<MembershipRulePreview, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${ruleBase(tenantID, groupId)}/preview`, {
          method: "POST",
        }).then((raw) => membershipRulePreviewSchema.parse(raw)),
      ),
  });
}

export function useActivateGroupRuleMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<
    MembershipRule,
    ApiError,
    { digest: string; acknowledgeBlastRadius: boolean }
  >({
    mutationFn: ({ digest, acknowledgeBlastRadius }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${ruleBase(tenantID, groupId)}/activate`, {
          method: "POST",
          body: { digest, acknowledge_blast_radius: acknowledgeBlastRadius },
        }).then((raw) => membershipRuleSchema.parse(raw)),
      ),
    onSuccess: () => invalidateRule(qc, tenantID, groupId),
  });
}

export function usePauseGroupRuleMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<MembershipRule, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${ruleBase(tenantID, groupId)}/pause`, {
          method: "POST",
        }).then((raw) => membershipRuleSchema.parse(raw)),
      ),
    onSuccess: () => invalidateRule(qc, tenantID, groupId),
  });
}

export function useDeleteGroupRuleMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<void>(ruleBase(tenantID, groupId), { method: "DELETE" }),
      ),
    onSuccess: () => invalidateRule(qc, tenantID, groupId),
  });
}

// ── Nesting (P4 slice 1) ──────────────────────────────────────────────────
//
// An edge extends the parent's grants to the child's people, so the write
// pair rides the same step-up ceremony as membership itself, and the act
// is previewed (blast radius) before it is authorized.

function nestingsBase(tenantID: string, groupId: string): string {
  return `${base(tenantID)}/${encodeURIComponent(groupId)}/nestings`;
}

export function useGroupNestingsQuery(tenantID: string, groupId: string) {
  return useQuery<GroupNestings, ApiError>({
    queryKey: queryKeys.groups.nestings(tenantID, groupId),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(nestingsBase(tenantID, groupId));
      return groupNestingsSchema.parse(raw);
    },
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

function invalidateNestings(qc: ReturnType<typeof useQueryClient>, tenantID: string, groupId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.groups.nestings(tenantID, groupId) });
  qc.invalidateQueries({ queryKey: queryKeys.groups.detail(tenantID, groupId) });
}

export function usePreviewNestGroupMutation(tenantID: string, groupId: string) {
  return useMutation<NestPreview, ApiError, string>({
    mutationFn: (childGroupId) =>
      withStepUp(() =>
        apiFetch<unknown>(`${nestingsBase(tenantID, groupId)}/preview`, {
          method: "POST",
          body: { child_group_id: childGroupId },
        }).then((raw) => nestPreviewSchema.parse(raw)),
      ),
  });
}

export function useNestGroupMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { childGroupId: string; acknowledgeBlastRadius: boolean }>({
    mutationFn: ({ childGroupId, acknowledgeBlastRadius }) =>
      withStepUp(() =>
        apiFetch<void>(nestingsBase(tenantID, groupId), {
          method: "POST",
          body: {
            child_group_id: childGroupId,
            acknowledge_blast_radius: acknowledgeBlastRadius,
          },
        }),
      ),
    onSuccess: () => invalidateNestings(qc, tenantID, groupId),
  });
}

// ── Reviewability (P4) ────────────────────────────────────────────────────
//
// Owners are accountability metadata; attest records that a human
// reviewed the group's access as it stands, clearing an overdue review.
// Type and review cadence changes go through the ordinary group PATCH.

export function useGroupOwnersQuery(tenantID: string, groupId: string) {
  return useQuery<readonly GroupOwner[], ApiError>({
    queryKey: queryKeys.groups.owners(tenantID, groupId),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(
        `${base(tenantID)}/${encodeURIComponent(groupId)}/owners`,
      );
      return groupOwnersResponseSchema.parse(raw).data;
    },
    enabled: tenantID.length > 0 && groupId.length > 0,
  });
}

export function useAddGroupOwnerMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (userId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(groupId)}/owners`, {
          method: "POST",
          body: { user_id: userId },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.owners(tenantID, groupId) });
    },
  });
}

export function useRemoveGroupOwnerMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (userId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(groupId)}/owners/${encodeURIComponent(userId)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.groups.owners(tenantID, groupId) });
    },
  });
}

export function useAttestGroupMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<Group, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(groupId)}/attest`, {
          method: "POST",
        }).then((raw) => groupSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.groups.detail(tenantID, groupId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.groups.list(tenantID) });
    },
  });
}

export function useUnnestGroupMutation(tenantID: string, groupId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (childGroupId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${nestingsBase(tenantID, groupId)}/${encodeURIComponent(childGroupId)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => invalidateNestings(qc, tenantID, groupId),
  });
}

export async function fetchMembershipRules(tenantID: string, cursor?: string): Promise<MembershipRules> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/membership-rules`, { searchParams: { cursor } });
  return membershipRulesSchema.parse(raw);
}

export function useMembershipRulesQuery(tenantID: string, cursor?: string) {
  return useQuery({
    queryKey: queryKeys.groups.ruleInventory(tenantID, cursor),
    queryFn: () => fetchMembershipRules(tenantID, cursor),
    enabled: tenantID.length > 0,
    staleTime: 0,
  });
}
